import React, { useEffect, useRef, useState } from 'react';
import { JOB_LABEL, cn, isOnline, presenceOf } from './model/derive';
import { useWorkboardCtx } from './model/useWorkboard';
import { TextArea } from './Modal';
import Linkify from './Linkify';
import { ToolTip } from '@repo/common';
import MemberCard from './MemberCard';

/**
 * 댓글 @멘션 · 이모지 반응.
 *   · 글을 쓰다 @ 를 치면 팀원 목록이 뜬다 (↑↓ · Enter/Tab 으로 고른다). 본문에는 "@이름" 으로 들어가고,
 *     보낼 때 본문에 남아 있는 "@이름" 만 멘션으로 보낸다 — 멘션된 사람과 태스크 담당자 모두에게 알림이 간다
 *   · 반응은 정해진 6개. 누르면 켜고 다시 누르면 끈다. 켜면 댓글 쓴 사람의 Inbox Activity 에 남는다 (토스트·소리 없음)
 */

export const REACTIONS = ['👍', '✅', '❤️', '😂', '🎉', '👀', '🙏'];

/** 멘션할 수 있는 사람 — 팀장 · 본인 · 이탈자 제외 */
export const mentionable = (memberById, viewerId, now) =>
  [...memberById.values()].filter(
    (m) => m.role !== 'npc' && m.id !== viewerId && presenceOf(m, now) !== 'dropped',
  );

/** 본문에 남아 있는 "@이름" 의 팀원 id */
export const mentionIdsIn = (text, people) =>
  people.filter((m) => text.includes(`@${m.name}`)).map((m) => m.id);

/** @ 뒤에 친 글자로 고르기 — 이름의 아무 단어나 그 글자로 시작하면 */
const matchesQuery = (name, q) =>
  !q ||
  name
    .toLowerCase()
    .split(/\s+/)
    .some((w) => w.startsWith(q.toLowerCase()));

// 글 칸 안 글자의 위치를 재려고 같은 모양의 보이지 않는 칸에 앞부분 글을 그려 본다
const MIRROR_PROPS = [
  'boxSizing',
  'width',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'lineHeight',
  'textTransform',
  'wordSpacing',
  'tabSize',
];

/** 글 칸 안에서 index 번째 글자의 위치 { top, left, height } — 글 칸 왼쪽 위 기준 (스크롤 반영) */
const caretAt = (el, index) => {
  const style = window.getComputedStyle(el);
  const mirror = document.createElement('div');
  MIRROR_PROPS.forEach((p) => {
    mirror.style[p] = style[p];
  });
  Object.assign(mirror.style, {
    position: 'absolute',
    visibility: 'hidden',
    top: '0',
    left: '-9999px',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
  });
  mirror.textContent = el.value.slice(0, index);
  const mark = document.createElement('span');
  mark.textContent = el.value.slice(index) || '.';
  mirror.appendChild(mark);
  document.body.appendChild(mirror);
  const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.4;
  const pos = {
    top: mark.offsetTop - el.scrollTop,
    left: mark.offsetLeft - el.scrollLeft,
    height: lineHeight,
  };
  document.body.removeChild(mirror);
  return pos;
};

const LIST_W = 280;

/** @멘션을 쓸 수 있는 글 칸 (TextArea 와 같은 모양) */
export const MentionTextArea = ({ value, onChange, onKeyDown, className, ...props }) => {
  const { data, memberById, now } = useWorkboardCtx();
  const people = mentionable(memberById, data.viewerId, now);
  const el = useRef(null);
  const [query, setQuery] = useState(null); // { start, text }
  const [active, setActive] = useState(0);
  const [at, setAt] = useState(null); // 목록 위치 — @ 글자 바로 아래

  /** 커서 바로 앞이 "@글자" 면 목록을 연다 */
  const detect = (target) => {
    el.current = target;
    const before = target.value.slice(0, target.selectionStart);
    const m = /(^|\s)@([^\s@]{0,20})$/.exec(before);
    const start = m ? target.selectionStart - m[2].length - 1 : null;
    setQuery(m ? { start, text: m[2] } : null);
    if (m) {
      const c = caretAt(target, start);
      // 칸 밖으로 넘치지 않게 왼쪽을 묶는다
      const left = Math.max(0, Math.min(c.left, target.clientWidth - LIST_W));
      setAt({ top: target.offsetTop + c.top + c.height + 4, left: target.offsetLeft + left });
    }
    setActive(0);
  };
  const matches = query ? people.filter((m) => matchesQuery(m.name, query.text)).slice(0, 6) : [];

  const pick = (m) => {
    const target = el.current;
    if (!target || !query) return;
    const caret = target.selectionStart;
    const insert = `@${m.name} `;
    onChange(value.slice(0, query.start) + insert + value.slice(caret));
    setQuery(null);
    const pos = query.start + insert.length;
    window.requestAnimationFrame(() => {
      target.focus();
      target.setSelectionRange(pos, pos);
    });
  };

  // 칸 밖을 누르면 닫는다
  useEffect(() => {
    if (!query) return undefined;
    const close = (e) => e.target !== el.current && setQuery(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [query]);

  return (
    <div className='relative'>
      <TextArea
        {...props}
        value={value}
        className={className}
        onChange={(e) => {
          onChange(e.target.value);
          detect(e.target);
        }}
        onClick={(e) => detect(e.target)}
        onKeyDown={(e) => {
          if (matches.length) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              const step = e.key === 'ArrowDown' ? 1 : -1;
              setActive((i) => (i + step + matches.length) % matches.length);
              return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault();
              pick(matches[active]);
              return;
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              setQuery(null);
              return;
            }
          }
          onKeyDown?.(e);
        }}
      />
      {matches.length > 0 && (
        <ul
          role='listbox'
          aria-label='Mention a teammate'
          style={at ? { top: at.top, left: at.left, width: LIST_W } : { width: LIST_W }}
          className='absolute z-30 rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-1 shadow-4'
        >
          {matches.map((m, i) => (
            <li
              key={m.id}
              role='option'
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(m);
              }}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-[13px]',
                i === active && 'bg-surface-green10',
              )}
            >
              <span
                className={cn(
                  'h-2 w-2 shrink-0 rounded-full',
                  isOnline(m, now, data.viewerId)
                    ? 'bg-functional-success03'
                    : 'bg-grayscale-gray04',
                )}
              />
              <span className='truncate font-medium text-text-primary'>{m.name}</span>
              <span className='shrink-0 text-text-secondary'>· {JOB_LABEL[m.job] ?? m.job}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** 본문 — 멘션한 "@이름" 은 초록 굵게, URL 은 링크 */
export const CommentText = ({ c }) => {
  const { memberById } = useWorkboardCtx();
  const idByName = new Map(
    (c.mentionIds ?? [])
      .map((id) => [memberById.get(id)?.name, id])
      .filter(([name]) => name),
  );
  const names = [...idByName.keys()].sort((a, b) => b.length - a.length);
  if (!names.length) return <Linkify text={c.body} />;
  const re = new RegExp(`(@(?:${names.map(escape).join('|')}))`, 'g');
  return c.body.split(re).map((part, i) =>
    part.startsWith('@') && idByName.has(part.slice(1)) ? (
      // 멘션을 누르면 그 팀원 카드 (사진 · 상태 · 작업 시간)
      <MemberCard
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        memberId={idByName.get(part.slice(1))}
        label={part}
        variant='mention'
        align='left'
      />
    ) : (
      // eslint-disable-next-line react/no-array-index-key
      <Linkify key={i} text={part} />
    ),
  );
};

/** 웃는 얼굴 + (반응 더하기) */
const SmileIcon = () => (
  <svg aria-hidden viewBox='0 0 16 16' className='h-4 w-4' fill='none' stroke='currentColor'>
    <circle cx='8' cy='8' r='6.25' strokeWidth='1.2' />
    <path
      d='M5.6 9.4c.6.9 1.4 1.35 2.4 1.35s1.8-.45 2.4-1.35'
      strokeWidth='1.2'
      strokeLinecap='round'
    />
    <circle cx='6' cy='6.6' r='.8' fill='currentColor' stroke='none' />
    <circle cx='10' cy='6.6' r='.8' fill='currentColor' stroke='none' />
  </svg>
);

/** 반응 줄 — 반응 칩(이모지 · 수) + 반응 더하기 */
export const Reactions = ({ c }) => {
  const { data, dispatch, memberById, now, readOnly, closed, actionsLocked } = useWorkboardCtx();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  // 방금 단 댓글(서버 id 를 받기 전)에는 반응하지 않는다
  const canReact = !readOnly && !closed && !actionsLocked && !/^c-\d+$/.test(c.id);
  const list = Object.entries(c.reactions ?? {}).filter(([, ids]) => ids?.length);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!list.length && !canReact) return null;
  const toggle = (emoji) => {
    dispatch({ type: 'reactComment', commentId: c.id, emoji, at: now.toISOString() });
    setOpen(false);
  };
  const names = (ids) => ids.map((id) => memberById.get(id)?.name ?? 'Someone').join(', ');
  // 나는 "You" 로, 맨 앞에
  const whoList = (ids) =>
    [...ids]
      .sort((a, b) => (b === data.viewerId) - (a === data.viewerId))
      .map((id) => (id === data.viewerId ? 'You' : (memberById.get(id)?.name ?? 'Someone')))
      .join(', ');

  return (
    <div className='flex flex-wrap items-center gap-1.5'>
      {list.map(([emoji, ids]) => {
        const mine = ids.includes(data.viewerId);
        return (
          // 마우스를 올리거나(키보드로 가면) 누가 달았는지 작은 창으로 보여 준다
          <span key={emoji} className='group/react relative'>
            <button
              type='button'
              aria-pressed={mine}
              disabled={!canReact}
              aria-label={`${emoji} ${ids.length} · ${names(ids)}`}
              onClick={() => toggle(emoji)}
              className={cn(
                'flex h-7 items-center gap-1 rounded-full border px-2 text-[13px] tabular-nums transition-colors disabled:cursor-default',
                mine
                  ? 'border-green-deep02/50 bg-surface-green10 text-green-deep04'
                  : 'border-green-deep03/[0.12] bg-grayscale-gray00 text-text-secondary enabled:hover:bg-grayscale-gray01',
              )}
            >
              <span aria-hidden>{emoji}</span>
              {ids.length}
            </button>
            {/* 디자인 시스템 툴팁 — 누가 반응했는지 */}
            <span
              role='tooltip'
              className='pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 w-max max-w-[240px] -translate-x-1/2 translate-y-0.5 opacity-0 transition-[opacity,transform] duration-150 group-focus-within/react:translate-y-0 group-focus-within/react:opacity-100 group-hover/react:translate-y-0 group-hover/react:opacity-100'
            >
              <ToolTip
                position='bottom'
                state='accent'
                line='multi'
                label={
                  <span className='flex flex-col'>
                    <span className='font-normal text-grayscale-gray03'>Reacted with {emoji}</span>
                    <span>{whoList(ids)}</span>
                  </span>
                }
              />
            </span>
          </span>
        );
      })}
      {canReact && (
        <span ref={ref} className='relative'>
          <button
            type='button'
            aria-label='Add reaction'
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            // 반응이 없어도 늘 보인다 (마우스를 올리지 않아도)
            className='flex h-7 items-center justify-center rounded-full px-1.5 text-icon-secondary hover:bg-grayscale-gray01 hover:text-text-primary'
          >
            <SmileIcon />
            <span aria-hidden className='-ml-0.5 text-[12px] leading-none'>
              +
            </span>
          </button>
          {open && (
            <span
              role='menu'
              aria-label='Reactions'
              className='absolute bottom-full left-0 z-30 mb-1 flex gap-0.5 rounded-full border border-green-deep03/[0.08] bg-grayscale-gray00 p-1 shadow-4'
            >
              {REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type='button'
                  role='menuitem'
                  aria-label={`React with ${emoji}`}
                  onClick={() => toggle(emoji)}
                  className='flex h-8 w-8 items-center justify-center rounded-full text-[17px] hover:bg-grayscale-gray01'
                >
                  {emoji}
                </button>
              ))}
            </span>
          )}
        </span>
      )}
    </div>
  );
};
